import * as React from "react";
import { Panel, FormGroup, ControlLabel, FormControl, HelpBlock, Row, Col } from "react-bootstrap";

interface IProps {
    conferenceNumber: string,
    conferenceNumberCode: string,
    conferenceNumberMobileFriendly: string,
    validation: {
        conferenceNumber: boolean,
        conferenceNumberCode: boolean,
        conferenceNumberMobileFriendly: boolean
    },
    onChange: (property: string, value: string) => void
}

class ConferenceCallPanel extends React.Component<IProps, {}> {
    handleDialInNumberChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        const dialInNumber = e.currentTarget.value;
        const mainAccessCode = this.getMainAccessCode();
        const fullNumber = mainAccessCode ? `${dialInNumber} code ${mainAccessCode}` : dialInNumber;
        this.props.onChange('conferenceNumber', fullNumber);
    }

    handleMainAccessCodeChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        const mainAccessCode = e.currentTarget.value;
        const dialInNumber = this.getDialInNumber();
        const fullNumber = mainAccessCode ? `${dialInNumber} code ${mainAccessCode}` : dialInNumber;
        this.props.onChange('conferenceNumber', fullNumber);
    }

    handleConferenceNumberCodeChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.props.onChange('conferenceNumberCode', e.currentTarget.value);
    }

    handleConferenceNumberMobileFriendlyChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.props.onChange('conferenceNumberMobileFriendly', e.currentTarget.value);
    }

    getDialInNumber = (): string => {
        const { conferenceNumber } = this.props;
        if (conferenceNumber.includes(' code ')) {
            return conferenceNumber.split(' code ')[0];
        }
        return conferenceNumber;
    }

    getMainAccessCode = (): string => {
        const { conferenceNumber } = this.props;
        if (conferenceNumber.includes(' code ')) {
            return conferenceNumber.split(' code ')[1];
        }
        return '';
    }

    render() {
        const { conferenceNumberCode, conferenceNumberMobileFriendly, validation } = this.props;
        const dialInNumber = this.getDialInNumber();
        const mainAccessCode = this.getMainAccessCode();

        const isSystem1 = dialInNumber.includes('1-800-444-2801');
        const isSystem2 = dialInNumber.includes('1-800-201-5203');
        
        return (
            <Panel bsStyle="info">
                <Panel.Heading>
                    <Panel.Title componentClass="h3">Conference Call Information</Panel.Title>
                </Panel.Heading>
                <Panel.Body>
                    <Row>
                        <Col xs={12} md={6}>
                            <FormGroup validationState={!validation.conferenceNumber ? 'error' : null}>
                                <ControlLabel>Dial-in Number *</ControlLabel>
                                <FormControl
                                    type="text"
                                    value={dialInNumber}
                                    onChange={this.handleDialInNumberChange}
                                    placeholder="e.g., 1-800-444-2801"
                                />
                                <HelpBlock>
                                    Enter just the dial-in number without any access codes.
                                </HelpBlock>
                            </FormGroup>
                        </Col>
                        <Col xs={12} md={6}>
                            <FormGroup validationState={!validation.conferenceNumber ? 'error' : null}>
                                <ControlLabel>Main Access Code *</ControlLabel>
                                <FormControl
                                    type="text"
                                    value={mainAccessCode}
                                    onChange={this.handleMainAccessCodeChange}
                                    placeholder="e.g., 8373308"
                                />
                                <HelpBlock>
                                    Enter the main access code.
                                </HelpBlock>
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup validationState={!validation.conferenceNumberCode ? 'error' : null}>
                        <ControlLabel>Secondary Access Code *</ControlLabel>
                        <FormControl
                            type="text"
                            value={conferenceNumberCode}
                            onChange={this.handleConferenceNumberCodeChange}
                            placeholder="e.g., 173 or 9095"
                        />
                        <HelpBlock>
                            Enter the secondary access code (separate from the main code).
                            {isSystem1 && <span> <strong>System 1 codes:</strong> Usually 3 digits (e.g., 173)</span>}
                            {isSystem2 && <span> <strong>System 2 codes:</strong> Usually 4 digits (e.g., 9095, 3711)</span>}
                        </HelpBlock>
                    </FormGroup>

                    <FormGroup validationState={!validation.conferenceNumberMobileFriendly ? 'error' : null}>
                        <ControlLabel>Mobile-Friendly Number</ControlLabel>
                        <FormControl
                            type="text"
                            value={conferenceNumberMobileFriendly}
                            onChange={this.handleConferenceNumberMobileFriendlyChange}
                            placeholder="e.g., 1-800-201-5203,,,532840"
                        />
                        <HelpBlock>
                            Optional: Mobile-friendly format with pauses (commas).
                            <br />
                            <strong>Format:</strong> 1-800-201-5203,,,XXXXXX (where commas create pauses)
                        </HelpBlock>
                    </FormGroup>

                    <div className="alert alert-info">
                        <strong>Conference Call Information Required:</strong>
                        <br />
                        This information is required for Operations, Swap Managers, and Duty Officers as it is used for coordinating swap logistics and emergency communications.
                        <br /><br />
                        <strong>Conference Call Systems:</strong>
                        <br />
                        <strong>System 1:</strong> 1-800-444-2801 (Senior Staff)
                        <br />
                        &nbsp;&nbsp;&nbsp;&nbsp;• Main Code: 8373308
                        <br />
                        &nbsp;&nbsp;&nbsp;&nbsp;• Secondary Code: 173
                        <br />
                        <strong>System 2:</strong> 1-800-201-5203 (Operations Staff)
                        <br />
                        &nbsp;&nbsp;&nbsp;&nbsp;• Main Code: Various (e.g., 532840, 622730)
                        <br />
                        &nbsp;&nbsp;&nbsp;&nbsp;• Secondary Code: Various (e.g., 9095, 3711)
                    </div>
                </Panel.Body>
            </Panel>
        );
    }
}

export default ConferenceCallPanel; 